import { StackedModal, StackedModalHeader } from "@/components/modal/stacked-modal";
import { FilledButton } from "@/components/ui/buttons/FilledButton";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { memo } from "react";
import { useShopListModal } from "../../hooks/use-shop-list-modal";
import { ShopListAisleTabs } from "../shop-list-aisle-tabs";
import { ShopListContent } from "../shop-list-content";

interface ShopListModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  pageRef: React.RefObject<HTMLElement>;
}

/**
 * Shopping list stacked modal.
 *
 * Layout (flex column, fills the full modal height):
 *   ┌─ StackedModalHeader  (fixed height, close + reset)
 *   ├─ ShopListAisleTabs   (fixed 38 px, horizontal scroll)
 *   ├─ ShopListContent     (flex-1, own vertical scroll)
 *   └─ Footer              (pinned, always visible)
 *
 * IonContent has scrollY={false} so the flex children own scrolling.
 */
export const ShopListModal = memo(function ShopListModal({
  isOpen,
  onDismiss,
  pageRef,
}: ShopListModalProps) {
  const {
    isLoading,
    tickedItems,
    selectedAisle,
    setSelectedAisle,
    handleToggleItem,
    handleMarkAllAsBought,
    handleResetList,
    isUpdating,
    groupedIngredients,
    ingredientCategories,
    selectedIngredients,
    error,
  } = useShopListModal();

  return (
    <StackedModal
      pageRef={pageRef}
      isOpen={isOpen}
      onDidDismiss={onDismiss}
      showHeader={false}
      scrollY={false}
      forceOverscroll={false}
      classNames={{ content: "!p-0" }}
    >
      {/* Outer wrapper takes the full height IonContent provides */}
      <div className="flex h-full flex-col">
        {/* ── Header ── */}
        <StackedModalHeader
          title="Your Shopping List"
          onClose={onDismiss}
          showConfirmButton={false}
          renderAction={
            <button
              type="button"
              onClick={handleResetList}
              disabled={isUpdating}
              className={cn(
                textVariants.footnote,
                "rounded-full border border-gray-300 px-4 py-2",
                "text-black transition-opacity duration-200",
                "disabled:cursor-not-allowed disabled:opacity-50",
              )}
            >
              Reset List
            </button>
          }
        />

        <ShopListAisleTabs
          aisles={ingredientCategories}
          selectedAisle={selectedAisle}
          groupedIngredients={groupedIngredients}
          onSelect={setSelectedAisle}
        />

        <ShopListContent
          isLoading={isLoading}
          error={error ?? null}
          selectedIngredients={selectedIngredients}
          tickedItems={tickedItems}
          handleToggleItem={handleToggleItem}
        />

        <div className="flex-shrink-0 bg-white px-4 pt-3 pb-[calc(var(--safe-area-inset-bottom)+1rem)]">
          <FilledButton
            color="black"
            text="Mark all as bought"
            textCase="normal"
            onClick={handleMarkAllAsBought}
            disabled={isUpdating}
          />
        </div>
      </div>
    </StackedModal>
  );
});
